/**
 * Build-cursor state: which mode the pointer is in, what cell it is over, and
 * what the sim said about the edit it would make.
 *
 * This is main-thread *interaction* state, and it is deliberately kept apart
 * from both the sim and the HUD store. What is built lives in the sim and
 * arrives in the snapshot; what the player is *about to* build lives here and
 * nowhere else. The one thing this file must never do is decide whether an edit
 * is legal — that answer comes from `host.probe`, which asks the same function
 * the command path runs (docs/13-architecture.md: no rule has two homes).
 *
 * It flushes on every change rather than at overlay rate, because a build
 * cursor that lags the pointer by 200 ms is a cursor nobody can aim.
 */

import type { RefusalUi, SiteCatalogUi } from '../bridge';

export type BuildMode =
  | { kind: 'inspect' }
  /** Build mode with nothing armed yet: the shelf is open, a click still selects. */
  | { kind: 'build' }
  | { kind: 'room'; typeId: string }
  /**
   * A machine armed for installation (M1.2: a *model* of a type, and — from
   * the used-market shelf — a second-hand one, whose price the campaign
   * stamps at the click). `modelId` absent means the type's reference model.
   */
  | { kind: 'tool'; typeId: string; modelId?: string; used?: boolean }
  | { kind: 'bulldoze' }
  /**
   * **Pick a machine up and put it down somewhere else** (B0.7). Two clicks:
   * one on the machine, one on where it should stand. It is its own mode rather
   * than a demolish-and-build because the sim treats it as its own edit — a
   * moved tool keeps its id, its queue and its history, and that is the whole
   * reason the lever exists.
   */
  | { kind: 'relocate' };

export interface Cell {
  x: number;
  y: number;
}

export interface BuildState {
  mode: BuildMode;
  catalog: SiteCatalogUi | null;
  /** Cell under the pointer, or `null` when it is off the plot. */
  cursor: Cell | null;
  /** Where a room drag started, while one is in progress. */
  anchor: Cell | null;
  /** 90° swap of a tool's authored footprint. */
  rotated: boolean;
  /** What the sim answered about the pending edit; `null` means legal. */
  verdict: RefusalUi | null;
  /** True between asking and hearing back. */
  asking: boolean;
  /**
   * The machine the relocate cursor has hold of, mirrored from the sim's own
   * selection (B0.7). It comes from the per-tick UI store rather than from the
   * snapshot, because *what a thing is* is something the overlay is supposed to
   * learn from the store — the same rule that made selection sim state at B0.2.
   */
  moving: MovingTool | null;
}

/** The machine under the relocate cursor, as the store reports it. */
export interface MovingTool {
  id: number;
  typeId: string;
  name: string;
  x: number;
  y: number;
  w: number;
  h: number;
  rotated: boolean;
}

const initial: BuildState = {
  mode: { kind: 'inspect' },
  catalog: null,
  cursor: null,
  anchor: null,
  rotated: false,
  verdict: null,
  asking: false,
  moving: null,
};

let state: BuildState = initial;
const listeners = new Set<() => void>();

export function buildState(): BuildState {
  return state;
}

export function setBuild(patch: Partial<BuildState>): void {
  const next = { ...state, ...patch };
  if (same(state, next)) return;
  state = next;
  for (const listener of listeners) listener();
}

export function subscribeBuild(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function same(a: BuildState, b: BuildState): boolean {
  return (
    a.mode === b.mode &&
    a.catalog === b.catalog &&
    a.rotated === b.rotated &&
    a.verdict === b.verdict &&
    a.asking === b.asking &&
    sameTool(a.moving, b.moving) &&
    sameCell(a.cursor, b.cursor) &&
    sameCell(a.anchor, b.anchor)
  );
}

function sameCell(a: Cell | null, b: Cell | null): boolean {
  return a === b || (!!a && !!b && a.x === b.x && a.y === b.y);
}

function sameTool(a: MovingTool | null, b: MovingTool | null): boolean {
  return (
    a === b ||
    (!!a && !!b && a.id === b.id && a.x === b.x && a.y === b.y && a.rotated === b.rotated)
  );
}

/** The block the cursor currently describes, in plot cells. */
export interface PendingRect {
  x: number;
  y: number;
  w: number;
  h: number;
  height: number;
}

/**
 * What the cursor would build, from the mode, the hovered cell and the drag.
 *
 * Room footprints are clamped to the type's authored bounds here so the ghost
 * shows a legal *size* while it is being dragged. That is presentation, not a
 * rule: the sim refuses an out-of-bounds footprint anyway, and this only means
 * the player cannot drag past what the catalog allows.
 */
export function pendingRect(state: BuildState): PendingRect | null {
  const { cursor, mode, catalog } = state;
  if (!cursor || !catalog) return null;
  switch (mode.kind) {
    case 'room': {
      const spec = catalog.rooms.find((room) => room.id === mode.typeId);
      if (!spec) return null;
      const anchor = state.anchor ?? cursor;
      const x0 = Math.min(anchor.x, cursor.x);
      const y0 = Math.min(anchor.y, cursor.y);
      const w = clamp(Math.abs(cursor.x - anchor.x) + 1, spec.minW, spec.maxW);
      const h = clamp(Math.abs(cursor.y - anchor.y) + 1, spec.minH, spec.maxH);
      // Dragging left or down grows away from the anchor, not through it.
      const x = cursor.x < anchor.x ? anchor.x - w + 1 : x0;
      const y = cursor.y < anchor.y ? anchor.y - h + 1 : y0;
      return { x, y, w, h, height: spec.height };
    }
    case 'tool': {
      const spec = catalog.tools.find((tool) => tool.id === mode.typeId);
      if (!spec) return null;
      const w = state.rotated ? spec.h : spec.w;
      const h = state.rotated ? spec.w : spec.h;
      return { x: cursor.x, y: cursor.y, w, h, height: spec.height };
    }
    case 'relocate': {
      // Nothing picked up yet: the cursor is a one-cell pointer looking for a
      // machine, and there is no ghost to draw.
      if (!state.moving) return null;
      const spec = catalog.tools.find((tool) => tool.id === state.moving?.typeId);
      if (!spec) return null;
      const w = state.rotated ? spec.h : spec.w;
      const h = state.rotated ? spec.w : spec.h;
      return { x: cursor.x, y: cursor.y, w, h, height: spec.height };
    }
    default:
      return { x: cursor.x, y: cursor.y, w: 1, h: 1, height: 0.5 };
  }
}

function clamp(value: number, low: number, high: number): number {
  return Math.min(Math.max(value, low), high);
}
